package com.crowpay.views.screens.auth

import com.crowpay.Address
import com.crowpay.Contractor
import com.crowpay.RegisterContractor
import com.crowpay.Resources
import com.crowpay.views.theming.AppColors
import com.crowpay.SharedUtils
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.contactEmail
import com.crowpay.contactFirstName
import com.crowpay.contactLastName
import com.crowpay.contactPhoneNumber
import com.crowpay.legalName
import com.crowpay.name
import com.crowpay.sdk.ApiOption
import com.crowpay.sdk.currentSession
import com.crowpay.sdk.notNullSession
import com.crowpay.sdk.selectedApi
import com.crowpay.sdk.sessionToken
import com.crowpay.selectedContractor
import com.crowpay.utils.pushChanges
import com.crowpay.utils.validation.Validator
import com.crowpay.utils.validation.validate
import com.crowpay.views.components.label2
import com.crowpay.views.AutoRoutes
import com.crowpay.views.components.nav.CrowPayDimensions
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.requiredField3
import com.crowpay.views.screens.common.LandingScreen
import com.crowpay.views.theming.*
import com.lightningkite.kiteui.QueryParameter
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.launchGlobal
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.Screen
import com.lightningkite.kiteui.navigation.render
import com.lightningkite.kiteui.navigation.screenNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.errorText
import com.lightningkite.lightningserver.auth.proof.FinishProof
import com.lightningkite.serialization.lensPath
import kotlinx.coroutines.delay


interface AuthScreen

@Routable("auth/log-in")
class LogInScreen : Screen, AuthScreen, Validator() {

    @QueryParameter
    val next: Property<String?> = Property(null)
    var nextScreen: Page? = null
        set(value) {
            field = value
            next.value = value?.let { AutoRoutes.render(it)?.urlLikePath?.render() }
        }

    val email = Property("")
    val passcodeSecret = Property<String?>(null)
    val passcode = Property("")
    val registering = Property(false)

    fun ViewWriter.registerForm(
        registering: Property<Boolean>
    ): ViewModifiable {
        val passCodeSecret = Property<String?>(null)
        val contractor = Draft {
            selectedContractor()?.let {
                currentSession()?.contractors?.get(it)?.invoke()
            } ?: Contractor(
                name = "",
                legalName = "",
                email = "",
                phoneNumber = "",
                address = Address(),
                contactFirstName = "",
                contactLastName = "",
                contactEmail = "",
                contactPhoneNumber = "",
                contactAddress = Address(),
                operationStates = setOf(),
                stateEntityNumber = "",
                ein = "",
                owners = listOf()
            )
        }

        val isLegalName = Property(true)

        val submitRegistration = Action(
            title = "REGISTER",
            icon = Icon.send,
            action = {
                if (isLegalName()) {
                    contractor.modify {
                        it.copy(
                            legalName = it.name
                        )
                    }
                }
                if (currentSession() == null) {
                    contractor.publish()
                    passCodeSecret set selectedApi().api.userAuth.sendRegistrationEmail(contractor().contactEmail)
                }
                else {
                    contractor.pushChanges(notNullSession().contractors)
                    screenNavigator.navigate(ContractorNewRegistration())
                }
            }
        )

        return stack {
            onlyWhen { passCodeSecret() == null } - col {
                title("Register Account")

                boldSubTitle("Primary Representative")
                requiredField3("First Name") {
                    val firstName = contractor.lensPath { it.contactFirstName }.validateNotBlank()
                    validate(firstName) - textField {
                        hint = "John"
                        keyboardHints = KeyboardHints.title
                        content bind firstName

                        action = submitRegistration
                    }
                }
                requiredField3("Last Name") {
                    val lastName = contractor.lensPath { it.contactLastName }.validateNotBlank()
                    validate(lastName) - textField {
                        hint = "Smith"
                        keyboardHints = KeyboardHints.title
                        content bind lastName

                        action = submitRegistration
                    }
                }
                requiredField3("Work Email") {
                    val contactEmail = contractor.lensPath { it.contactEmail }
                        .validate { it.matches(SharedUtils.emailPattern) }
                    validate(contactEmail) - textField {
                        hint = "john@company.com"
                        keyboardHints = KeyboardHints.email
                        content bind contactEmail

                        action = submitRegistration
                    }
                }
                requiredField3("Cell Phone", "Must be a cellphone for authentication") {
                    val contactPhone =
                        contractor.lensPath { it.contactPhoneNumber }.validate { it.matches(SharedUtils.phonePattern) }
                    validate(contactPhone) - phoneNumberInput {
                        hint = "(123) 456-7890"
                        content bind contactPhone
                    }
                }
                greySeparator()
                boldSubTitle("Business Name(s)")
                requiredField3(
                    "Trade Name or DBA",
                    "This is the business name your clients know you by. This will be your CrowPay account name."
                ) {
                    val contractorName = contractor.lensPath { it.name }.validateNotBlank()
                    validate(contractorName) - textField {
                        hint = "Enter Company Name"
                        keyboardHints = KeyboardHints.title
                        content bind contractorName

                        action = submitRegistration
                    }
                }
                label2("Is this also the registered legal name?") {
                    row {
                        centered - radioButton {
                            checked bind isLegalName
                        }
                        centered - body("Yes")
                        centered - radioButton {
                            checked bind isLegalName.equalTo(false)
                        }
                        centered - body("No")
                    }
                }
                onlyWhen { !isLegalName() } - requiredField3("Business Legal Name") {
                    val legalContractorName = contractor.lensPath { it.legalName }.validateNotBlank()
                    validate(legalContractorName) - textField {
                        hint = "Enter Legal Name"
                        keyboardHints = KeyboardHints.title
                        content bind legalContractorName

                        action = submitRegistration
                    }
                }
                space()
                col {
                    primaryButton - button {
                        ::enabled {
                            val draft = contractor()
                            draft.contactFirstName.isNotBlank() && draft.contactLastName.isNotBlank() &&
                                    draft.contactEmail.matches(SharedUtils.emailPattern) &&
                                    draft.contactPhoneNumber.matches(SharedUtils.phonePattern) &&
                                    draft.name.isNotBlank() && (isLegalName() || draft.legalName.isNotBlank())
                        }
                        body {
                            align = Align.Center
                            content = "Register"
                        }
                        action = submitRegistration
                    }
                    errorText()
                }
                centered - row {
                    spacing = 0.5.rem
                    body("Already have an account? ")
                    colored(AppColors.secondary.main) - button {
                        spacing = 1.dp
                        body("Sign in")
                        onClick {
                            registering set false
                        }
                    }
                }
            }
            onlyWhen { passCodeSecret() != null } - col {
                EmailProof(
                    { passCodeSecret() ?: "" },
                    { contractor().contactEmail },
                    { passCodeSecret set null }
                ) { proof ->
                    val api = selectedApi().api

                    api.contractor.register(RegisterContractor(contractor(), proof))

                    val session = api.userAuth.logIn(listOf(proof))
                    sessionToken set session.session!!
                    selectedContractor set contractor()._id

                    screenNavigator.navigate(ContractorNewRegistration())
                }.render(this)
            }
        }
    }


    override fun ViewWriter.render() {

        if (nextScreen == null && next.value != null) next.value = null

        val sendEmail = Action(
            title = "LOG IN",
            icon = Icon.login,
            action = {
                passcodeSecret.value = selectedApi.await().api.emailProof.beginEmailOwnershipProof(email.await())
            }
        )

        suspend fun confirmPasscode() {
            val api = selectedApi.await().api
            val proof = api.emailProof.proveEmailOwnership(
                FinishProof(
                    passcodeSecret.awaitOnce() ?: return,
                    passcode.awaitOnce()
                )
            )
            val session = api.userAuth.logIn(listOf(proof))
            sessionToken.set(session.session!!)
            delay(2000)

            if (nextScreen != null)
                screenNavigator.reset(nextScreen!!)
            else
                screenNavigator.reset(LandingScreen())
        }

        AuthBackgroundSemantic.onNext - scrolls - stack {
            spacing = 8.dp
            centered - padded - rowCollapsingToColumn(AppDimensions.rerenderWidth) {
                centered - loginBanner()
                space()
                AuthDialogSemantic.onNext - centered - stack {
                    spacing = AppDimensions.sectionIndent
                    appTheme.onNext - sizeConstraints(width = 30.rem) - col {

                        onlyWhen { next() != null } - body("Please login to continue to your project")

                        onlyWhen { passcodeSecret() == null && !registering() } - col {
                            title("Log In")

                            label2("Email") {
                                fieldTheme - textField {
                                    hint = "Email"
                                    keyboardHints = KeyboardHints.email
                                    content bind email

                                    action = sendEmail
                                }
                            }

                            space()

                            primaryButton - button {
                                ::enabled{ email().matches(SharedUtils.emailPattern) }
                                body {
                                    align = Align.Center
                                    content = "Log In"
                                }
                                action = sendEmail
                            }

                            centered - row {
                                spacing = 0.5.rem
                                body("Are you a contractor? ")
                                colored(AppColors.secondary.main) - button {
                                    spacing = 1.dp
                                    body("Sign Up")
                                    onClick {
                                        registering set true
                                    }
                                }
                            }
                        }
                        onlyWhen { passcodeSecret() != null } - col {
                            title("Log In")

                            body {
                                ::content{
                                    "An email with a passcode was sent to ${email()}. Enter the passcode below to confirm login."
                                }
                            }

                            label2("Passcode") {
                                fieldTheme - textField {
                                    hint = "Passcode"
                                    keyboardHints = KeyboardHints.id
                                    content bind passcode

                                    action = Action(
                                        title = "LOG IN",
                                        icon = Icon.login,
                                        action = ::confirmPasscode,
                                    )
                                }
                            }
                            space()
                            col {
                                primaryButton - button {
                                    ::enabled{ passcode().length == 6 }
                                    body {
                                        align = Align.Center
                                        content = "Log In"
                                    }
                                    onClick(action = ::confirmPasscode)
                                }
                                errorText()
                            }
                            smallBody("If you did not receive a code, please check the email address provided or try again.")
                            centered - textButton - button {
                                val showIcon = Property(false)
                                row {
                                    centered - smallBody {
                                        align = Align.Center
                                        content = "Send me a new code."
                                    }

                                    centered - onlyWhen { showIcon() } - icon {
                                        source = Icon.done.copy(width = 1.rem, height = 1.rem)
                                        description = "Done"
                                    }
                                }
                                onClick {
                                    passcodeSecret.value = selectedApi.await().api.emailProof
                                        .beginEmailOwnershipProof(email.awaitOnce())
                                    passcode.value = ""

                                    launchGlobal {
                                        showIcon.set(true)
                                        delay(3000)
                                        showIcon.set(false)
                                    }
                                }
                            }

                            centered - textButton - button {
                                smallBody {
                                    align = Align.Center
                                    content = "Use a different email."
                                }
                                onClick {
                                    passcodeSecret set null
                                    passcode set ""
                                }
                            }
                        }
                        onlyWhen(false) { registering() } - registerForm(registering)
                    }
                }
            }

            gravity(Align.End, Align.Start) - button {
                sizeConstraints(minWidth = 6.rem) - body {
                    align = Align.Center
                    ::content{
                        selectedApi().apiName
                    }
                }

                onClick {
                    selectedApi.value = selectedApi.value.next()
                }
            }
        }
    }
}

fun ViewWriter.loginBanner() =
    sizeConstraints(width = 30.rem) - col {
        sizeConstraints(height = 4.rem, width = 19.135.rem) - centered - image {
            source = Resources.imagesFullLogoColor
        }

        centered - body {
            content = "Contractor's Project Escrow Solution!"
        }
    }
